import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { usePayrollPending } from "../context/PayrollPendingContext";
import { formatApiError } from "../lib/api";
import { inr } from "../lib/format";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "./ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Wallet, RotateCcw, AlertTriangle } from "lucide-react";

/** The top-bar call to action (Task 18): a plain wallet icon, quiet until there is something to do — a
 *  count badge appears only when count > 0 and pulses gently (CSS .pulse-badge, index.css — dropped
 *  entirely under prefers-reduced-motion). Renders nothing at all when the integration is unconfigured, so
 *  a deployment that never turns this on shows no trace of it. Clicking opens the pending panel.
 *
 *  Wave review, cross-cutting: `!pending?.configured` used to be the only thing checked here, so a
 *  deployment that deliberately never configured payroll and one whose payroll config or connection is
 *  broken looked identical — both rendered nothing, with no way for an admin to tell the difference.
 *  `fetchFailed` (PayrollPendingContext) is only ever true after a real failure — a clean "not configured"
 *  response never sets it — so this now shows a small, quiet, non-pulsing warning glyph for exactly that
 *  case, with the reason in its title/aria-label, while staying fully invisible for the untouched-by-design
 *  case this component's silence was built for. */
export function PayrollPendingIndicator() {
  const pending = usePayrollPending();
  const [open, setOpen] = useState(false);
  if (!pending?.configured) {
    if (!pending?.fetchFailed) return null;
    return (
      <span
        data-testid="payroll-pending-fetch-failed"
        title="Payroll sync failed — check the payroll server configuration and connectivity"
        aria-label="Payroll sync failed — check the payroll server configuration and connectivity"
        className="h-9 w-9 shrink-0 rounded-md border border-line flex items-center justify-center text-warn"
      >
        <AlertTriangle size={17} strokeWidth={1.75} />
      </span>
    );
  }
  const { count } = pending;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="payroll-pending-indicator"
        aria-label={count > 0 ? `${count} payroll cash voucher${count === 1 ? "" : "s"} waiting to be raised` : "Payroll cash obligations"}
        title="Payroll cash obligations"
        className="relative h-9 w-9 shrink-0 rounded-md border border-line flex items-center justify-center text-ink hover:bg-paper-alt transition-colors"
      >
        <Wallet size={17} strokeWidth={1.75} />
        {count > 0 && (
          <span
            data-testid="payroll-pending-count"
            className="pulse-badge absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-warn text-white text-[10px] font-bold flex items-center justify-center leading-none"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>
      <PayrollPendingPanel open={open} onOpenChange={setOpen} />
    </>
  );
}

function Row({ label, v, strong }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 border-b border-line last:border-0 text-sm">
      <span className="label-eyebrow shrink-0 pt-0.5">{label}</span>
      <span className={`text-right ${strong ? "font-mono font-semibold" : ""}`}>{v}</span>
    </div>
  );
}

function whenApproved(iso) {
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

function PayrollPendingPanel({ open, onOpenChange }) {
  const { items, loading, refresh, createVoucher, syncNow } = usePayrollPending();
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [created, setCreated] = useState(null);
  const navigate = useNavigate();

  // A deliberate pull every time the panel is opened by a person — not a timer, and not on every render.
  useEffect(() => { if (open) refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open]);

  const doSync = async () => {
    setSyncing(true);
    try {
      const r = await syncNow();
      if (r.new > 0) toast.success(`${r.new} new obligation${r.new === 1 ? "" : "s"} found`);
      else toast.info("Nothing new from payroll");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSyncing(false); }
  };

  const doCreate = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      const data = await createVoucher(confirm.id);
      toast.success(`Voucher ${data.doc_no} created`);
      setConfirm(null);
      setCreated(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const gotoPrint = () => {
    const n = created?.doc_no ? parseInt(created.doc_no.slice(3), 10) : null;
    onOpenChange(false);
    setCreated(null);
    navigate(n ? `/vouchers/print?series=CV&number=${n}` : "/vouchers/print");
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-md flex flex-col" data-testid="payroll-pending-panel">
          <div className="flex items-start justify-between gap-3 pr-8">
            <div>
              <SheetTitle>Payroll Cash Obligations</SheetTitle>
              <SheetDescription>Approved payroll pay runs, advances and loans waiting to be raised as a cash voucher here.</SheetDescription>
            </div>
            <Button size="sm" variant="outline" onClick={doSync} disabled={syncing} data-testid="payroll-sync-now-btn" className="shrink-0">
              <RotateCcw size={13} className={`mr-1.5 ${syncing ? "animate-spin" : ""}`} /> Sync Now
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto -mx-6 px-6 mt-2 space-y-3" data-testid="payroll-pending-list">
            {loading && items.length === 0 && <div className="text-sm text-muted py-10 text-center">Loading…</div>}
            {!loading && items.length === 0 && <div className="text-sm text-muted py-10 text-center">Nothing waiting right now.</div>}
            {items.map((it) => (
              <Card key={it.id} className="p-4 border border-line" data-testid={`payroll-pending-item-${it.id}`}>
                <div className="label-eyebrow">{it.source_label}</div>
                <div className="font-medium mt-0.5">{it.reference}</div>
                {it.kind === "pay_run" && (
                  <div className="text-xs text-muted mt-1">{it.cash_employee_count} cash-paid employee{it.cash_employee_count === 1 ? "" : "s"}</div>
                )}
                <div className="text-xs text-muted mt-0.5">Approved {whenApproved(it.approved_at)}</div>
                <div className="flex items-center justify-between mt-3">
                  <div className="font-mono font-semibold text-lg">{inr(it.amount)}</div>
                  <Button size="sm" onClick={() => setConfirm(it)} data-testid={`payroll-create-btn-${it.id}`} className="bg-ink text-white">
                    Create Cash Voucher
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent data-testid="payroll-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Create Cash Voucher</DialogTitle>
            <DialogDescription>This posts a real cash outflow against today&apos;s ledger. Review before confirming.</DialogDescription>
          </DialogHeader>
          {confirm && (
            <div>
              <Row label="Source" v={confirm.source_label} />
              <Row label="Reference" v={confirm.reference} />
              {confirm.kind === "pay_run" && <Row label="Period" v={`${confirm.period_from} to ${confirm.period_to}`} />}
              {confirm.kind === "pay_run" && <Row label="Cash-paid employees" v={confirm.cash_employee_count} />}
              <Row label="Amount" v={inr(confirm.amount)} strong />
              <Row label="Particulars to be written" v={confirm.preview_particulars} />
              <Row label="Ledger to be debited" v={confirm.preview_ledger} />
              <Row label="Payroll approved" v={whenApproved(confirm.approved_at)} />
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button onClick={doCreate} disabled={busy} data-testid="payroll-confirm-create-btn" className="bg-cashout hover:bg-cashout/90 text-white">
              {busy ? "Creating…" : "Confirm & Create Voucher"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!created} onOpenChange={(o) => !o && setCreated(null)}>
        <DialogContent data-testid="payroll-created-dialog">
          <DialogHeader>
            <DialogTitle>Voucher {created?.doc_no} created</DialogTitle>
            <DialogDescription>It has been removed from the pending list. Printing is a separate, deliberate step, whenever you&apos;re ready.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCreated(null)}>Close</Button>
            <Button onClick={gotoPrint} data-testid="payroll-goto-print-btn" className="bg-ink text-white">Go to Print Vouchers</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
